import { memo, useCallback, useState, type ReactElement } from 'react';
import IconWrap from '@ubnt/ui-components/Icon/Icon';
import { ButtonIcon } from '@ubnt/ui-components/aria/experimental/Button';
import { Tooltip_Default } from '@ubnt/ui-components/aria/experimental/Tooltip';
import { useTimeoutFn } from 'react-use';
import { useApplicationStore } from 'modules/application/store';

import COLORS from 'styles/colors.module.scss';

export interface ToggleButtonProps {
  onClick: () => void;
  children: ReactElement;
  tooltipDescription: string;
  tooltipTitle: string;
}

const ToggleButton = (props: ToggleButtonProps) => {
  const { onClick, children, tooltipDescription, tooltipTitle } = props;
  const isMobile = useApplicationStore((state) => state.isMobile);
  const [isOpen, setIsOpen] = useState(false);
  const [, , reset] = useTimeoutFn(() => setIsOpen(false), 1000);
  const onPress = useCallback(() => {
    onClick();
    if (isMobile) {
      setIsOpen(true);
      reset();
    }
  }, [onClick, isMobile, reset]);

  return (
    <Tooltip_Default
      offset={10}
      isOpen={isOpen}
      description={tooltipDescription}
      title={isMobile ? undefined : tooltipTitle}
    >
      <ButtonIcon
        aria-label={tooltipTitle}
        data-testid={`toggle-${tooltipTitle.toLowerCase().replace(/ /g, '-')}`}
        variant="neutral"
        onPress={onPress}
        size="small"
        onHoverChange={setIsOpen}
      >
        <IconWrap size="medium" color={COLORS.neutral08}>
          {children}
        </IconWrap>
      </ButtonIcon>
    </Tooltip_Default>
  );
};

const MemoizedToggleButton = memo(ToggleButton);

export default MemoizedToggleButton;
